import { useRef, useEffect, useCallback, useState } from 'react';
import { useTab, MAX_TABS } from '@platform/app-shell/frontend/contexts/TabContext';
import { dropIndexAt } from '@platform/app-shell/frontend/utils/tabOrder';
import { ToastStack, useToasts } from '@platform/ui/Toast';
import { useTheme } from '@platform/app-shell/frontend/contexts/ThemeContext';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { isAdminUser } from '@platform/app-shell/frontend/utils/adminAuth';
import SiteSwitcher from '@platform/app-shell/frontend/components/SiteSwitcher';
import LanguageButton from '@platform/translator/frontend/LanguageButton';
import '@platform/app-shell/frontend/components/TabBar.css';

const DRAG_THRESHOLD = 5;
const TEAR_OFF_DISTANCE = 40;
// While dragging, a pointer this close to either end of the strip scrolls it.
const EDGE_SCROLL_ZONE = 28;
const EDGE_SCROLL_STEP = 10;

export default function TabBar() {
  const { tabs, activeTabId, isPopoutWindow, switchTab, closeTab, moveTab, limitHits, popOutTab, dockTab, adminMode, toggleAdminMode } = useTab();
  const { isDark, toggleTheme } = useTheme();
  const { userGroups } = useAuth();
  const dragRef = useRef(null);
  const tabBarRef = useRef(null);
  const stripRef = useRef(null);
  // Sideways drag: the tab being moved and the slot it would land before
  // (null = no marker: not dragging, tearing off, or a drop on its own spot).
  const [drag, setDrag] = useState({ id: null, dropIndex: null });
  // The strip scrolls once the tabs reach their minimum width: whether it
  // overflows (draws the ‹ › buttons) and which ends have more to show.
  const [scroll, setScroll] = useState({ overflow: false, left: false, right: false });
  const { toasts, push, dismiss } = useToasts();

  const cleanup = useCallback(() => {
    const state = dragRef.current;
    if (!state) return;
    if (state.clone) {
      state.clone.remove();
    }
    document.body.classList.remove('tab-dragging');
    dragRef.current = null;
    setDrag({ id: null, dropIndex: null });
  }, []);

  const measureScroll = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    const left = overflow && el.scrollLeft > 1;
    const right = overflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setScroll((prev) => (prev.overflow === overflow && prev.left === left && prev.right === right
      ? prev : { overflow, left, right }));
  }, []);

  const scrollStrip = useCallback((dir) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(120, el.clientWidth * 0.7), behavior: 'smooth' });
  }, []);

  // Re-measure when tabs come and go and when the bar resizes.
  useEffect(() => { measureScroll(); }, [tabs.length, measureScroll]);
  useEffect(() => {
    const el = stripRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measureScroll());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureScroll]);

  // A mouse wheel scrolls the strip sideways. Non-passive so the page under
  // the bar does not scroll too; a wheel on a strip that fits does nothing.
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (!delta) return;
      e.preventDefault();
      el.scrollLeft += delta;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // The active tab is always in view — opened at the end, switched to from
  // a menu, or restored on reload.
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const tabEl = el.querySelector(`[data-tab-id="${activeTabId}"]`);
    if (!tabEl) return;
    // The strip is positioned (TabBar.css), so offsetLeft is within it.
    const left = tabEl.offsetLeft;
    const right = left + tabEl.offsetWidth;
    // Clear of the 24px edge fade (TabBar.css), not just on screen.
    const margin = 28;
    if (left - margin < el.scrollLeft) el.scrollLeft = left - margin;
    else if (right + margin > el.scrollLeft + el.clientWidth) el.scrollLeft = right - el.clientWidth + margin;
    measureScroll();
    // scroll.overflow: the ‹ › buttons arriving narrow the strip — look again.
  }, [activeTabId, tabs.length, scroll.overflow, measureScroll]);

  // A tab refused at the cap says so (TabContext openTab / openBackgroundTab).
  useEffect(() => {
    if (limitHits > 0) push('warning', `You have ${MAX_TABS} tabs open. Close one to open another.`);
  }, [limitHits, push]);

  const tearOff = useCallback((tabId, screenX, screenY) => {
    cleanup();
    if (isPopoutWindow) {
      dockTab(tabId);
    } else {
      popOutTab(tabId, { screenX, screenY });
    }
  }, [cleanup, isPopoutWindow, popOutTab, dockTab]);

  const handlePointerDown = useCallback((e, tab) => {
    // Left button only, mouse only
    if (e.button !== 0 || e.pointerType !== 'mouse') return;
    // Ignore clicks on the close button
    if (e.target.closest('.tab-close')) return;

    // In main window with 1 tab, nothing to drag — let click handle it
    const canTearOff = tabs.length > 1 || isPopoutWindow;
    if (!canTearOff) return;

    e.preventDefault();

    dragRef.current = {
      tab,
      index: tabs.findIndex((t) => t.id === tab.id),
      dropIndex: null,
      startX: e.clientX,
      startY: e.clientY,
      lastScreenX: e.screenX,
      lastScreenY: e.screenY,
      isDragging: false,
      isTearingOff: false,
      tabEl: e.currentTarget,
      clone: null,
    };
  }, [tabs, isPopoutWindow]);

  useEffect(() => {
    const handlePointerMove = (e) => {
      const state = dragRef.current;
      if (!state) return;

      state.lastScreenX = e.screenX;
      state.lastScreenY = e.screenY;

      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;

      // Check drag threshold
      if (!state.isDragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        state.isDragging = true;
        document.body.classList.add('tab-dragging');

        // Create floating clone
        const rect = state.tabEl.getBoundingClientRect();
        const clone = state.tabEl.cloneNode(true);
        clone.className = 'tab-item tab-floating-clone';
        clone.style.width = rect.width + 'px';
        clone.style.height = rect.height + 'px';
        document.body.appendChild(clone);
        state.clone = clone;
      }

      // Position clone at cursor
      if (state.clone) {
        state.clone.style.left = (e.clientX - 40) + 'px';
        state.clone.style.top = (e.clientY - 14) + 'px';
      }

      // Tear-off condition: far below tab bar
      const tabBarRect = tabBarRef.current?.getBoundingClientRect();
      const belowBar = tabBarRect ? (e.clientY > tabBarRect.bottom + TEAR_OFF_DISTANCE) : false;

      if (belowBar !== state.isTearingOff) {
        state.isTearingOff = belowBar;
        if (state.clone) {
          state.clone.classList.toggle('tear-off-ready', belowBar);
        }
      }

      // Sideways: where the tab would land. Hidden while tearing off — the
      // drop then opens a window, it does not move the tab.
      const strip = stripRef.current;
      let dropIndex = null;
      if (!belowBar && strip) {
        const sr = strip.getBoundingClientRect();
        if (e.clientX < sr.left + EDGE_SCROLL_ZONE) strip.scrollLeft -= EDGE_SCROLL_STEP;
        else if (e.clientX > sr.right - EDGE_SCROLL_ZONE) strip.scrollLeft += EDGE_SCROLL_STEP;
        const rects = Array.from(strip.querySelectorAll('.tab-item')).map((el) => el.getBoundingClientRect());
        dropIndex = dropIndexAt(rects, e.clientX);
        const from = rects.length ? state.index : -1;
        if (dropIndex === from || dropIndex === from + 1) dropIndex = null;
      }
      if (dropIndex !== state.dropIndex) {
        state.dropIndex = dropIndex;
        setDrag({ id: state.tab.id, dropIndex });
      }
    };

    const handlePointerUp = (e) => {
      const state = dragRef.current;
      if (!state) return;

      if (state.isDragging && state.isTearingOff) {
        tearOff(state.tab.id, state.lastScreenX, state.lastScreenY);
      } else if (!state.isDragging) {
        // Was just a click, not a drag — the tab's onClick switches.
        cleanup();
      } else {
        // Dragged sideways: the tab moves to the marked slot (none marked =
        // dropped on its own spot, nothing moves).
        const { dropIndex } = state;
        const id = state.tab.id;
        cleanup();
        if (dropIndex != null) moveTab(id, dropIndex);
      }
    };

    // When cursor leaves the window during a drag → tear off immediately
    // (like Chrome: dragging a tab out of the window creates a new window)
    const handlePointerLeave = (e) => {
      const state = dragRef.current;
      if (!state) return;
      if (!state.isDragging) {
        // Mouse left before drag threshold — just cancel
        cleanup();
        return;
      }
      // Use the leave event's screen coordinates for window placement
      tearOff(state.tab.id, e.screenX, e.screenY);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && dragRef.current) {
        cleanup();
      }
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.documentElement.addEventListener('pointerleave', handlePointerLeave);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.documentElement.removeEventListener('pointerleave', handlePointerLeave);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [cleanup, tearOff, switchTab, moveTab]);

  return (
    <div className="tab-bar" ref={tabBarRef}>
      {scroll.overflow && (
        <button
          type="button"
          className="tab-scroll tab-scroll-left"
          onClick={() => scrollStrip(-1)}
          disabled={!scroll.left}
          title="Scroll tabs left"
          aria-label="Scroll tabs left"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
        </button>
      )}
      <div
        className={`tab-bar-tabs${scroll.left ? ' fade-left' : ''}${scroll.right ? ' fade-right' : ''}`}
        ref={stripRef}
        onScroll={measureScroll}
      >
        {tabs.map((tab, index) => (
          <div
            key={tab.id}
            data-tab-id={tab.id}
            className={`tab-item ${tab.id === activeTabId ? 'active' : ''}${drag.id === tab.id ? ' drag-source' : ''}${drag.dropIndex === index ? ' drop-before' : ''}${drag.dropIndex === tabs.length && index === tabs.length - 1 ? ' drop-after' : ''}`}
            title={tab.title}
            onPointerDown={(e) => handlePointerDown(e, tab)}
            // Every switch goes through click. The drag code above is
            // mouse-only, so a touch tap (iPad, iPhone, Android tablet, a
            // touchscreen laptop, a pen) never reached switchTab while it
            // lived in pointerup — tabs could not be changed by finger.
            onClick={() => switchTab(tab.id)}
            onAuxClick={(e) => {
              if (e.button === 1) {
                e.preventDefault();
                e.stopPropagation();
                closeTab(tab.id);
              }
            }}
          >
            <span className="tab-title">{tab.title}</span>
            <button
              className="tab-close"
              onClick={(e) => {
                e.stopPropagation();
                closeTab(tab.id);
              }}
              title="Close tab"
            >
              ×
            </button>
          </div>
        ))}
      </div>
      {scroll.overflow && (
        <button
          type="button"
          className="tab-scroll tab-scroll-right"
          onClick={() => scrollStrip(1)}
          disabled={!scroll.right}
          title="Scroll tabs right"
          aria-label="Scroll tabs right"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
        </button>
      )}
      {/* Trailing chrome — grouped so the CSS can rule a hairline between the
          tab strip and the controls instead of letting them run together. */}
      <div className="tab-bar-actions">
      {/* The site switcher (site-switchers group only; renders nothing
          otherwise) sits with the other always-on chrome, before the theme
          toggle — it is not an admin affordance. */}
      <SiteSwitcher />
      <button
        className={`tab-theme-toggle ${isDark ? 'is-dark' : 'is-light'}`}
        onClick={toggleTheme}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {/* Two stacked icons crossfade — sun visible in dark mode (the
            action it triggers), moon visible in light mode. */}
        <span className="tab-theme-icon tab-theme-icon-sun" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="4"/>
            <path d="M12 2v2"/>
            <path d="M12 20v2"/>
            <path d="m4.93 4.93 1.41 1.41"/>
            <path d="m17.66 17.66 1.41 1.41"/>
            <path d="M2 12h2"/>
            <path d="M20 12h2"/>
            <path d="m6.34 17.66-1.41 1.41"/>
            <path d="m19.07 4.93-1.41 1.41"/>
          </svg>
        </span>
        <span className="tab-theme-icon tab-theme-icon-moon" aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        </span>
      </button>
      {/* The Translator's dictionary icon — draws nothing unless a site admin switched it on */}
      <LanguageButton />
      {isAdminUser(userGroups) && (
        <button
          className={`tab-admin ${adminMode ? 'active' : ''}`}
          onClick={toggleAdminMode}
          title={adminMode ? 'Exit admin mode' : 'Enter admin mode'}
        >
          {adminMode ? '🔓' : '🔒'}
        </button>
      )}
      </div>
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
